import { useMutation } from '@tanstack/react-query';
import { UploadCloud } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui';
import { gisApi, type UploadResult, type UploadedFeature } from '@/lib/api';
import { cn } from '@/lib/cn';

interface GISUploadProps {
  onSubmit: (geom: GeoJSON.Geometry, label: string) => void;
  pending: boolean;
}

export function GISUpload({ onSubmit, pending }: GISUploadProps) {
  const { t } = useTranslation('studio');
  const [selected, setSelected] = useState<UploadedFeature | null>(null);

  const parse = useMutation<UploadResult, Error, File>({
    mutationFn: (file) => gisApi.upload(file),
    onSuccess: () => setSelected(null),
  });

  const onFile = (file: File | null) => {
    if (!file) return;
    parse.mutate(file);
  };

  return (
    <div className="space-y-3">
      <label className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-slate-300 px-4 py-8 text-center hover:border-accent">
        <UploadCloud className="h-6 w-6 text-slate-400" aria-hidden />
        <span className="text-body text-slate-700">{t('gis.editor.upload.label')}</span>
        <span className="text-small text-slate-500">{t('gis.editor.upload.formats')}</span>
        <input
          type="file"
          accept=".geojson,.json,.kml,.zip"
          className="hidden"
          onChange={(e) => onFile(e.target.files?.[0] ?? null)}
        />
      </label>

      {parse.isPending && (
        <p className="text-small text-slate-500">{t('gis.editor.upload.parsing')}</p>
      )}

      {parse.isError && (
        <p className="rounded border border-red-200 bg-red-50 px-3 py-2 text-small text-red-700">
          {parse.error.message}
        </p>
      )}

      {parse.data && (
        <div className="space-y-2">
          <p className="text-small text-slate-600">
            {t('gis.editor.upload.parsed', { count: parse.data.feature_count })}
            {parse.data.reprojected_from && (
              <span className="ml-2 text-slate-500">
                ({t('gis.editor.upload.reprojected', { from: parse.data.reprojected_from })})
              </span>
            )}
          </p>
          {parse.data.feature_count === 0 ? (
            <p className="text-small text-slate-500">{t('gis.editor.upload.noFeatures')}</p>
          ) : (
            <>
              <p className="text-h4 text-slate-700">{t('gis.editor.upload.pickFeature')}</p>
              <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded border border-slate-200">
                {parse.data.features.map((f, i) => (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => setSelected(f)}
                      className={cn(
                        'flex w-full items-center justify-between gap-3 px-3 py-2 text-left transition-colors hover:bg-slate-50',
                        selected === f && 'bg-brand-50',
                      )}
                    >
                      <span className="flex-1">
                        <p className="text-body text-slate-900">{f.name ?? '—'}</p>
                      </span>
                      <span className="font-mono text-small text-slate-500">{f.geom_type}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      <div className="flex justify-end">
        <Button
          onClick={() =>
            selected && onSubmit(selected.geom_geojson, selected.name ?? selected.geom_type)
          }
          disabled={!selected || pending}
        >
          {t('gis.editor.save')}
        </Button>
      </div>
    </div>
  );
}
